import React, { useEffect, useState, useCallback } from 'react';
import { ListPlus, Plus, Trash2, Loader2 } from 'lucide-react';
import api from '@/lib/axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const TYPE_LABELS = {
  text: 'Text',
  number: 'Zahl',
  date: 'Datum',
  select: 'Auswahl',
  checkbox: 'Häkchen',
};

export const CustomFieldsManager = () => {
  const [entityType, setEntityType] = useState('student');
  const [defs, setDefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({ label: '', field_type: 'text', options: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/custom-fields?entity_type=${entityType}&include_inactive=true`);
      setDefs(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [entityType]);

  useEffect(() => { load(); }, [load]);

  const createField = async () => {
    if (!draft.label.trim()) {
      toast.error('Bezeichnung ist erforderlich');
      return;
    }
    const options = draft.field_type === 'select'
      ? draft.options.split(',').map((o) => o.trim()).filter(Boolean)
      : [];
    if (draft.field_type === 'select' && options.length === 0) {
      toast.error('Auswahlfelder benötigen Optionen (kommagetrennt)');
      return;
    }
    setCreating(true);
    try {
      await api.post('/custom-fields', {
        entity_type: entityType,
        label: draft.label.trim(),
        field_type: draft.field_type,
        options,
        sort_order: defs.length,
      });
      toast.success('Zusatzfeld angelegt');
      setShowDialog(false);
      setDraft({ label: '', field_type: 'text', options: '' });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Anlegen');
    } finally {
      setCreating(false);
    }
  };

  const toggleActive = async (d) => {
    try {
      await api.put(`/custom-fields/${d.id}`, { is_active: !d.is_active });
      setDefs((p) => p.map((x) => (x.id === d.id ? { ...x, is_active: !d.is_active } : x)));
    } catch {
      toast.error('Fehler beim Speichern');
    }
  };

  const deleteField = async (d) => {
    if (!window.confirm(`Zusatzfeld „${d.label}" inkl. aller erfassten Werte löschen?`)) return;
    try {
      await api.delete(`/custom-fields/${d.id}`);
      setDefs((p) => p.filter((x) => x.id !== d.id));
      toast.success('Zusatzfeld gelöscht');
    } catch {
      toast.error('Fehler beim Löschen');
    }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 mb-6" data-testid="custom-fields-manager">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <ListPlus className="w-6 h-6 text-sky-600" />
          <div>
            <h2 className="text-xl font-semibold text-slate-900">Zusatzfelder</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Frei definierbare Felder für Schüler und Verträge — gelten für den aktiven Mandanten
            </p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={() => setShowDialog(true)} data-testid="add-custom-field-btn">
          <Plus className="h-4 w-4 mr-2" />
          Feld hinzufügen
        </Button>
      </div>

      <div className="flex gap-2 mb-4">
        <Button
          variant={entityType === 'student' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setEntityType('student')}
          data-testid="custom-fields-tab-student"
          className={entityType === 'student' ? 'bg-slate-900 text-white' : ''}
        >
          Schüler
        </Button>
        <Button
          variant={entityType === 'contract' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setEntityType('contract')}
          data-testid="custom-fields-tab-contract"
          className={entityType === 'contract' ? 'bg-slate-900 text-white' : ''}
        >
          Verträge
        </Button>
      </div>

      <div className="border rounded-lg overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-slate-50 border-b">
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Bezeichnung</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Typ</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Optionen</th>
              <th className="text-center px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Aktiv</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="text-center py-6 text-slate-500"><Loader2 className="w-5 h-5 animate-spin inline" /></td></tr>
            ) : defs.length === 0 ? (
              <tr><td colSpan={5} className="text-center py-6 text-sm text-slate-500" data-testid="custom-fields-empty-row">
                Noch keine Zusatzfelder für {entityType === 'student' ? 'Schüler' : 'Verträge'} definiert
              </td></tr>
            ) : (
              defs.map((d) => (
                <tr key={d.id} className="border-b last:border-0" data-testid={`custom-field-row-${d.id}`}>
                  <td className="px-4 py-3 text-sm font-medium text-slate-800">{d.label}</td>
                  <td className="px-4 py-3 text-sm"><Badge variant="outline">{TYPE_LABELS[d.field_type] || d.field_type}</Badge></td>
                  <td className="px-4 py-3 text-xs text-slate-500">{(d.options || []).join(', ') || '—'}</td>
                  <td className="px-4 py-3 text-center">
                    <Switch checked={d.is_active} onCheckedChange={() => toggleActive(d)} data-testid={`custom-field-active-${d.id}`} />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button variant="ghost" size="sm" onClick={() => deleteField(d)} data-testid={`custom-field-delete-${d.id}`}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md" data-testid="add-custom-field-dialog">
          <DialogHeader>
            <DialogTitle>Zusatzfeld anlegen</DialogTitle>
            <DialogDescription>
              Für {entityType === 'student' ? 'Schüler' : 'Verträge'} im aktiven Mandanten
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Bezeichnung *</Label>
              <Input
                value={draft.label}
                onChange={(e) => setDraft((p) => ({ ...p, label: e.target.value }))}
                placeholder="z. B. Instrument geliehen seit"
                data-testid="custom-field-label-input"
              />
            </div>
            <div>
              <Label>Feldtyp</Label>
              <Select value={draft.field_type} onValueChange={(v) => setDraft((p) => ({ ...p, field_type: v }))}>
                <SelectTrigger data-testid="custom-field-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(TYPE_LABELS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {draft.field_type === 'select' && (
              <div>
                <Label>Optionen (kommagetrennt) *</Label>
                <Input
                  value={draft.options}
                  onChange={(e) => setDraft((p) => ({ ...p, options: e.target.value }))}
                  placeholder="z. B. Anfänger, Fortgeschritten, Profi"
                  data-testid="custom-field-options-input"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Abbrechen</Button>
            <Button onClick={createField} disabled={creating} data-testid="custom-field-create-submit"
                    className="bg-slate-900 text-white hover:bg-slate-800">
              {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Anlegen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
